import { supabase } from './supabaseClient';

/**
 * Load all users (admin/sales_manager only)
 */
export async function loadUsers() {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('❌ Error loading users:', err);
    throw err;
  }
}

/**
 * Create new user with secure credentials
 */
export async function createUser(userData) {
  if (!supabase) return null;
  
  try {
    // Use the password provided by admin, or generate one if not provided
    const passwordToSave = userData.password || generateSecurePassword();
    
    // New users are NOT approved by default - admin must approve them
    const { data, error } = await supabase
      .from('users')
      .insert([{
        name: userData.name,
        username: userData.username,
        password_hash: passwordToSave,
        role: userData.role,
        phone: userData.phone || null,
        commission_per_deal: userData.commission_per_deal || null,
        is_approved_to_work: false, // Default: not approved
        approved_at: null,
        approved_by: null,
      }])
      .select()
      .single();
    
    if (error) throw error;
    
    // Return user with the password used (for sharing with user)
    return { ...data, tempPassword: passwordToSave };
  } catch (err) {
    console.error('❌ Error creating user:', err);
    throw err;
  }
}

/**
 * Update user information
 */
export async function updateUser(userId, updates) {
  if (!supabase) return null;
  
  try {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('❌ Error updating user:', err);
    throw err;
  }
}

/**
 * Delete user
 */
export async function deleteUser(userId) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Error deleting user:', err);
    throw err;
  }
}

/**
 * Approve user to work (allow them to see contacts)
 */
export async function approveUser(userId, adminId) {
  if (!supabase) return null;
  
  try {
    const { data, error } = await supabase
      .from('users')
      .update({
        is_approved_to_work: true,
        approved_at: new Date().toISOString(),
        approved_by: adminId,
      })
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('❌ Error approving user:', err);
    throw err;
  }
}

/**
 * Revoke user approval (prevent them from seeing contacts)
 */
export async function revokeUserApproval(userId) {
  if (!supabase) return null;
  
  try {
    const { data, error } = await supabase
      .from('users')
      .update({
        is_approved_to_work: false,
        approved_at: null,
        approved_by: null,
      })
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('❌ Error revuser approval:', err);
    throw err;
  }
}

/**
 * Authenticate user
 * Supports both email and username login
 */
export async function authenticateUser(identifier, password) {
  if (!supabase) return null;
  
  try {
    // Query users table - support both email and username
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .or(`email.eq.${identifier},username.eq.${identifier}`)
      .limit(1);
    
    // If query failed
    if (error) {
      console.error('❌ Query error:', error.message);
      return null;
    }
    
    // If no user found
    if (!data || data.length === 0) {
      console.warn('⚠️ User not found:', identifier);
      return null;
    }
    
    // Get the first user
    const user = data[0];
    
    // Check password match (database uses password_hash column)
    const userPassword = user.password_hash || user.password;
    if (!userPassword || userPassword !== password) {
      console.warn('⚠️ Incorrect password for user:', identifier);
      return null;
    }
    
    // Update last_login and set online
    await supabase
      .from('users')
      .update({ 
        last_login: new Date().toISOString(),
        is_online: true,
        last_active: new Date().toISOString(),
      })
      .eq('id', user.id);
    
    console.log('✅ Login successful:', user.email || user.username);
    return user;
  } catch (err) {
    console.error('❌ Error authenticating user:', err);
    return null;
  }
}

/**
 * Set user offline (on logout)
 */
export async function setUserOffline(userId) {
  if (!supabase) return;
  
  try {
    await supabase
      .from('users')
      .update({ 
        is_online: false,
        last_active: new Date().toISOString(),
      })
      .eq('id', userId);
  } catch (err) {
    console.error('⚠️ Failed to set user offline:', err);
  }
}

/**
 * Generate secure random password
 */
function generateSecurePassword(length = 12) {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}
